import { useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import { IconButton, Menu, MenuItem, TableCell, TableRow } from '@mui/material'
import Content from 'components/content/content'
import Icon from 'components/icon'
import CONSTANT from 'modules/constant'
import GLOBAL from 'modules/global'
import type { ErrorProps } from 'modules/types'
import type { ReferralHistory, PartialReferralHistory } from 'modules/referral/referral.dto'
import Notification from 'components/notification'
import { useGetReferralHistoryListMutation } from 'store/referral'
import Detail from './detail/detail'

const ReferralList = () => {
  const [getListSubmissions, submissionsList] = useGetReferralHistoryListMutation()

  const [payload, setPayload] = useState<PartialReferralHistory>({
    start: 0,
    length: CONSTANT.DEFAULT_PAGINATION_PER_PAGE,
    search: ''
  })

  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const [menu, setMenu] = useState<null | number>(null)
  const [drawerDetail, setDrawerDetail] = useState(false)

  const onSearch = (value: string) => setPayload({ ...payload, start: 0, search: value })
  const onChangePage = (value: number) => setPayload({ ...payload, start: value - 1 })
  const onChangePerPage = (value: number) => setPayload({ ...payload, length: value })

  const onAction = (event: React.MouseEvent<HTMLElement>, index: number) => {
    setAnchor(event.currentTarget)
    setMenu(index)
  }

  const onRemoveAnchor = (callback: () => void) => {
    setAnchor(null)
    callback()
  }

  const getField = () =>
    columns
      .filter((c) => c.searchable !== false)
      .map((c) => c.key)
      .join(',')

  const onResync = () => {
    const formData = new FormData()

    formData.append('start', payload.start.toString())
    formData.append('length', payload.length.toString())
    formData.append('field', getField())

    if (!isEmpty(payload.search)) formData.append('search[value]', payload.search)

    getListSubmissions(formData)
  }

  const columns = [
    {
      column: 'Member Id',
      label: 'Member Code',
      searchable: false,
      key: 'memberCode'
    },
    {
      column: 'Full Name',
      label: 'Full Name',
      searchable: false,
      key: 'fullName'
    },
    {
      column: 'Relationship',
      label: 'Relationship',
      searchable: true,
      key: 'relationshipType'
    },
    {
      column: 'Name of the Referred Person',
      label: 'Name of the Referred Person',
      searchable: true,
      key: 'referredName'
    },
    {
      column: 'Referred Phone Number',
      label: 'Referred Phone Number',
      searchable: true,
      key: 'referredPhoneNumber'
    },
    {
      column: 'Province of the Referred Person',
      label: 'Province of the Referred Person',
      searchable: true,
      key: 'provinceCode'
    },
    {
      column: 'Preferred Dealer',
      label: 'Preferred Dealer',
      searchable: true,
      key: 'preferredDealerCode'
    },
    {
      column: 'Hyundai Preferred Model',
      label: 'Hyundai Preferred Model',
      searchable: false,
      key: 'preferredHyundaiModel'
    },
    {
      column: 'Form Submission Date',
      label: 'Form Submission Date',
      searchable: false,
      key: 'createdAt',
      render: (row: ReferralHistory) => GLOBAL.formatDateWithTime(row.createdAt)
    }
  ]

  const tableHeadTitles = ['Actions', ...columns.map((c) => c.label)]
  const openAnchor = Boolean(anchor)

  const totalPage = submissionsList.data && GLOBAL.tableTotalPage(submissionsList.data.recordsTotal)
  const numbers = GLOBAL.tableNumber(payload.start)

  useEffect(() => {
    onResync()
  }, [payload])

  return (
    <>
      <Content
        title='Referral Submissions'
        tableHeadTitles={tableHeadTitles}
        onSearch={onSearch}
        onResync={onResync}
        pagination={onChangePage}
        onChangePerPage={onChangePerPage}
        totalPage={totalPage}
        totalRecords={submissionsList.data && submissionsList.data.recordsTotal}
        page={payload.start + 1}
        isLoading={submissionsList.isLoading}
        useAction={false}
      >
        {submissionsList.isSuccess &&
          submissionsList.data &&
          submissionsList.data.data?.map((table, index) => (
            <TableRow key={index}>
              <TableCell>{numbers[index] + 1}</TableCell>
              <TableCell>
                <IconButton
                  id={`button-${index}-${table.id}`}
                  aria-haspopup='true'
                  aria-controls={openAnchor ? `menu-${index}-${table.id}` : undefined}
                  aria-expanded={openAnchor ? 'true' : undefined}
                  onClick={(event) => onAction(event, index)}
                >
                  <Icon icon='MoreHoriz' />
                </IconButton>
                <Menu
                  id={`menu-${index}-${table.id}`}
                  aria-labelledby={`button-${index}-${table.id}`}
                  anchorEl={anchor}
                  open={openAnchor && menu == index}
                  onClose={() => onRemoveAnchor(() => setMenu(null))}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                  }}
                >
                  <MenuItem onClick={() => onRemoveAnchor(() => setDrawerDetail(true))}>
                    View Detail
                  </MenuItem>
                </Menu>
                <Detail
                  id={table.id}
                  open={drawerDetail && menu == index}
                  onClose={() => setDrawerDetail(false)}
                />
              </TableCell>
              {columns.map((col) => (
                <TableCell key={col.key}>
                  {col.render
                    ? col.render(table as ReferralHistory)
                    : table[col.key as keyof ReferralHistory] || '-'}
                </TableCell>
              ))}
            </TableRow>
          ))}
      </Content>

      <Notification
        open={submissionsList.isError}
        onClose={() => {
          submissionsList.reset()
        }}
        isError={submissionsList.isError}
        message={GLOBAL.returnExceptionMessage(
          submissionsList.isError,
          submissionsList.error as ErrorProps
        )}
      />
    </>
  )
}

export default ReferralList
